import { z } from 'zod';
import { fieldCompanyControlsSchema } from './field-controls.js';
import { fieldDiscoveryActionSchemas, fieldDiscoverySchema, fieldOutcomeIdSchema, fieldTicketPrioritySchema, } from './field-discovery.js';
import { fieldBrowserObservationSchema } from './field-observation.js';
import { fieldProgressSchema } from './field-progress.js';
import { fieldReviewReportSchema, fieldReviewStageSchema } from './field-review-report.js';
import { fieldShareSessionSchema, fieldSharingActionSchemas, fieldSubscriptionSchema, } from './field-sharing.js';
import { fieldWorkspaceCaptureRequestSchema } from './field-workspace.js';
export const fieldActorIdSchema = z.enum(['jordan', 'marcus', 'elena', 'noor', 'amara']);
/** One authority map for document requests, authoring templates and UI choices. */
export const FIELD_DOCUMENT_TEMPLATES_BY_ACTOR = {
    noor: {
        architecture: 'architecture-comment',
        decision: 'architecture-comment',
        test_plan: 'fixture-note',
        report: 'pr-review-summary',
        requirements: 'contract-addendum',
    },
    elena: {
        policy: 'policy-clarification',
        decision: 'policy-clarification',
        requirements: 'approval-matrix',
        report: 'reportability-assessment',
    },
    jordan: {
        requirements: 'user-story',
        scope: 'scope-charter-draft',
        report: 'retrospective',
        handoff: 'demo-agenda',
    },
    marcus: {
        scope: 'commercial-assumptions',
        requirements: 'commercial-assumptions',
        decision: 'priority-decision',
        report: 'acceptance-letter',
    },
    amara: {
        runbook: 'readiness-checklist',
        postmortem: 'incident-report',
        handoff: 'handoff-result',
        report: 'alert-summary',
        test_plan: 'questionnaire-cover',
    },
};
export const FIELD_DOCUMENT_KINDS_BY_ACTOR = {
    noor: Object.keys(FIELD_DOCUMENT_TEMPLATES_BY_ACTOR.noor),
    elena: Object.keys(FIELD_DOCUMENT_TEMPLATES_BY_ACTOR.elena),
    jordan: Object.keys(FIELD_DOCUMENT_TEMPLATES_BY_ACTOR.jordan),
    marcus: Object.keys(FIELD_DOCUMENT_TEMPLATES_BY_ACTOR.marcus),
    amara: Object.keys(FIELD_DOCUMENT_TEMPLATES_BY_ACTOR.amara),
};
export function canAuthorFieldDocument(actor, kind) {
    return Object.hasOwn(FIELD_DOCUMENT_TEMPLATES_BY_ACTOR[actor], kind);
}
const id = z.string().min(1).max(120);
const time = z.string();
export const fieldActorSchema = z.object({
    id: fieldActorIdSchema,
    name: z.string(),
    role: z.string(),
    focus: z.string(),
});
export const fieldMessageSchema = z.object({
    id,
    actorId: fieldActorIdSchema,
    role: z.enum(['learner', 'colleague', 'system']),
    text: z.string(),
    createdAt: time,
    sourceIds: z.array(z.string()),
    operationId: z.string().nullable(),
    meetingId: id.optional(),
    /** Server-created batch confirmation references; never accepted on a chat action. */
    decisionRequests: z
        .array(z.object({ decisionId: id, version: z.number().int().positive() }))
        .optional(),
});
export const fieldTicketSchema = z.object({
    id,
    title: z.string(),
    description: z.string(),
    day: z.number().int(),
    owner: fieldActorIdSchema,
    status: z.enum(['backlog', 'in_progress', 'in_review', 'done', 'deferred']),
    acceptance: z.array(z.string()),
    dependencies: z.array(z.string()),
    version: z.number().int().positive().optional(),
    priority: fieldTicketPrioritySchema.optional(),
    decisionIds: z.array(id).optional(),
    outcomeIds: z.array(fieldOutcomeIdSchema).optional(),
    findingId: id.optional(),
    origin: z.enum(['learner', 'review']).optional(),
    deferralReason: z.string().optional(),
});
export const fieldReviewSchema = z.object({
    id,
    actorId: fieldActorIdSchema,
    revision: z.string(),
    status: z.enum(['pending', 'changes_requested', 'reviewed', 'approved', 'stale', 'failed']),
    summary: z.string(),
    createdAt: time,
    operationId: id.optional(),
    stage: fieldReviewStageSchema.optional(),
    progress: z
        .object({ completed: z.number().int(), total: z.number().int(), detail: z.string() })
        .optional(),
    modelId: z.string().optional(),
    report: fieldReviewReportSchema.optional(),
});
export const fieldPrSchema = z.object({
    id,
    title: z.string(),
    description: z.string(),
    ticketIds: z.array(z.string()),
    revision: z.string(),
    provenance: z.enum(['learner_upload', 'workspace_capture']),
    fileCount: z.number().int(),
    status: z.enum(['open', 'merged', 'closed']),
    reviews: z.array(fieldReviewSchema),
    /** Derived from the current candidate's changed paths and ticket ownership. */
    requiredReviewers: z.array(fieldActorIdSchema).optional(),
    createdAt: time,
    updatedAt: time,
});
export const fieldDocumentSchema = z.object({
    id,
    title: z.string(),
    kind: z.string(),
    version: z.number().int(),
    author: z.union([fieldActorIdSchema, z.literal('learner')]),
    status: z.enum(['draft', 'published', 'superseded']),
    sourceIds: z.array(z.string()),
    formats: z.array(z.enum(['markdown', 'html', 'json', 'yaml', 'csv', 'pdf'])),
    createdAt: time,
    deliveries: z.array(z.object({ id, actorId: fieldActorIdSchema, deliveredAt: time })),
});
export const fieldOperationSchema = z.object({
    id,
    kind: z.enum(['turn', 'document', 'review', 'analysis', 'run', 'assessment']),
    status: z.enum(['pending', 'running', 'completed', 'failed']),
    summary: z.string(),
    error: z.string().nullable(),
    createdAt: time,
    completedAt: time.nullable(),
});
export const fieldRunSchema = z.object({
    operationId: id.optional(),
    id,
    prId: z.string(),
    revision: z.string(),
    suite: z.enum(['contracts', 'security', 'evaluation', 'acceptance', 'rollback', 'demo']),
    status: z.enum(['pending', 'running', 'passed', 'failed', 'unavailable']),
    trusted: z.boolean(),
    summary: z.string(),
    checks: z.array(z.object({ name: z.string(), passed: z.boolean(), detail: z.string() })),
    createdAt: time,
    browserObservation: fieldBrowserObservationSchema.optional(),
});
export const fieldAssessmentSchema = z.object({
    status: z.enum(['not_requested', 'blocked', 'ready_for_moderation', 'completed']),
    score: z.number().nullable(),
    gates: z.array(z.object({ id, label: z.string(), passed: z.boolean(), evidenceIds: z.array(z.string()) })),
    feedback: z.string(),
    evaluatedAt: time.nullable(),
});
export const fieldMeetingSchema = z.object({
    id,
    title: z.string(),
    actorIds: z.array(fieldActorIdSchema),
    status: z.enum(['active', 'ended']),
    startedAt: time,
    endedAt: time.nullable(),
    notes: z.string(),
});
export const fieldIncidentSchema = z.object({
    id,
    title: z.string(),
    severity: z.enum(['SEV2', 'SEV3']),
    status: z.enum(['open', 'mitigated', 'resolved']),
    updates: z.array(z.object({ text: z.string(), createdAt: time })),
    createdAt: time,
});
export const fieldSnapshotSchema = z.object({
    enabled: z.boolean(),
    engagement: z.object({
        id,
        title: z.string(),
        scenarioVersion: z.string(),
        status: z.enum(['active', 'completed', 'closed']),
        day: z.number().int().min(1).max(10),
        revision: z.number().int(),
        startedAt: time,
        updatedAt: time,
    }),
    actors: z.array(fieldActorSchema),
    messages: z.array(fieldMessageSchema),
    tickets: z.array(fieldTicketSchema),
    prs: z.array(fieldPrSchema),
    documents: z.array(fieldDocumentSchema),
    operations: z.array(fieldOperationSchema),
    runs: z.array(fieldRunSchema),
    meetings: z.array(fieldMeetingSchema),
    incidents: z.array(fieldIncidentSchema),
    shares: z.array(fieldShareSessionSchema).optional(),
    subscriptions: z.array(fieldSubscriptionSchema).optional(),
    controls: fieldCompanyControlsSchema.optional(),
    discovery: fieldDiscoverySchema.optional(),
    progress: fieldProgressSchema.optional(),
    assessment: fieldAssessmentSchema,
    capabilities: z.object({
        chat: z.boolean(),
        voice: z.boolean(),
        documents: z.boolean(),
        review: z.boolean(),
        observations: z.boolean(),
        workspace: z.boolean(),
    }),
    workspace: z.object({
        href: z.string().nullable(),
        instructions: z.string(),
        starterDownload: z.string(),
    }),
    release: z.object({
        status: z.enum(['not_requested', 'blocked', 'ready', 'released', 'paused', 'rolled_back']),
        blockers: z.array(z.string()),
        revision: z.string().nullable(),
        prId: z.string().optional(),
    }),
});
const fileSchema = z.object({
    path: z
        .string()
        .min(1)
        .max(240)
        .refine((p) => !p.startsWith('/') &&
        !p.split('/').some((s) => s === '..' || s === '.' || s === '') &&
        !p.includes('\\') &&
        !p.includes('\0'), 'Relative project path required'),
    content: z.string().max(200_000),
});
const files = z
    .array(fileSchema)
    .min(1)
    .max(120)
    .refine((xs) => new Set(xs.map((x) => x.path)).size === xs.length, 'Duplicate file paths');
export const fieldCommandSchema = z.object({
    requestId: z.uuid(),
    expectedRevision: z.number().int().nonnegative(),
    action: z.discriminatedUnion('type', [
        ...fieldSharingActionSchemas,
        ...fieldDiscoveryActionSchemas,
        z.object({
            type: z.literal('progress.visit'),
            seenRevision: z.number().int().nonnegative(),
        }),
        z.object({ type: z.literal('operation.cancel'), operationId: id }),
        z.object({ type: z.literal('operation.retry'), operationId: id }),
        z.object({ type: z.literal('standup.post'), text: z.string().trim().min(1).max(8000) }),
        z.object({
            type: z.literal('delivery.record'),
            title: z.string().trim().min(1).max(200),
            text: z.string().trim().min(1).max(64000),
            sourceIds: z.array(id).max(30),
            kind: z.enum([
                'scope',
                'decision',
                'runbook',
                'postmortem',
                'handoff',
                'test_plan',
                'report',
            ]),
            actorIds: z.array(fieldActorIdSchema).max(5),
        }),
        z.object({
            type: z.literal('message'),
            actorId: fieldActorIdSchema,
            text: z.string().trim().min(1).max(12000),
            sourceIds: z.array(id).max(20).default([]),
            modality: z.enum(['chat', 'voice']).default('chat'),
            meetingId: id.optional(),
        }),
        z.object({
            type: z.literal('ticket.update'),
            ticketId: id,
            status: fieldTicketSchema.shape.status,
            reason: z.string().trim().min(1).max(4000).optional(),
        }),
        z.object({
            type: z.literal('pr.open'),
            title: z.string().trim().min(1).max(200),
            description: z.string().max(12000),
            ticketIds: z.array(id).max(20),
            files,
            reviewerIds: z.array(fieldActorIdSchema).min(1).max(5).optional(),
        }),
        z.object({
            type: z.literal('pr.revise'),
            prId: id,
            files,
            reviewerIds: z.array(fieldActorIdSchema).min(1).max(5).optional(),
        }),
        z.object({
            type: z.literal('pr.merge'),
            prId: id,
            revision: z.string().regex(/^[a-f0-9]{64}$/),
        }),
        z.object({
            type: z.literal('pr.request_review'),
            prId: id,
            revision: z.string().regex(/^[a-f0-9]{64}$/),
            actorIds: z.array(fieldActorIdSchema).min(1).max(5),
            purpose: z.string().max(4000),
        }),
        z.object({
            type: z.literal('document.request'),
            actorId: fieldActorIdSchema,
            kind: z.enum([
                'requirements',
                'policy',
                'architecture',
                'runbook',
                'decision',
                'report',
                'handoff',
                'postmortem',
                'test_plan',
                'scope',
            ]),
            title: z.string().min(1).max(200),
            instructions: z.string().max(8000),
            sourceIds: z.array(id).max(30).default([]),
        }),
        z.object({
            type: z.literal('document.share'),
            documentId: id,
            actorIds: z.array(fieldActorIdSchema).min(1).max(5),
        }),
        z.object({ type: z.literal('run.request'), prId: id, suite: fieldRunSchema.shape.suite }),
        z.object({
            type: z.literal('meeting.start'),
            title: z.string().min(1).max(200),
            actorIds: z.array(fieldActorIdSchema).min(1).max(5),
        }),
        z.object({ type: z.literal('meeting.end'), meetingId: id, notes: z.string().max(16000) }),
        z.object({
            type: z.literal('incident.update'),
            incidentId: id,
            text: z.string().min(1).max(8000),
            status: fieldIncidentSchema.shape.status.optional(),
            expectedStatus: fieldIncidentSchema.shape.status.optional(),
        }),
        z.object({ type: z.literal('day.advance') }),
        z.object({ type: z.literal('release.request'), prId: id }),
        z.object({ type: z.literal('release.rollback'), reason: z.string().min(1).max(4000) }),
        z.object({ type: z.literal('assessment.request') }),
    ]),
});
export const fieldSpeechRequestSchema = z.object({ messageId: id });
export const fieldVoiceTokenSchema = z.object({ token: z.string(), expiresAt: z.string() });
export const fieldRubricDimensionSchema = z.enum([
    'discovery',
    'architecture',
    'ai_behavior',
    'implementation',
    'evaluation',
    'reliability',
    'security',
    'collaboration',
    'operations',
    'handoff',
]);
export const fieldModerationSchema = z.object({
    checkpointDay: z.union([z.literal(5), z.literal(10)]).optional(),
    requestId: z.uuid(),
    expectedRevision: z.number().int().nonnegative(),
    prId: z.uuid(),
    revision: z.string().regex(/^[a-f0-9]{64}$/),
    ratings: z
        .array(z.object({
        dimension: fieldRubricDimensionSchema,
        rating: z.number().int().min(0).max(4),
        rationale: z.string().min(20).max(4000),
        evidenceIds: z.array(z.string()).min(1).max(20),
    }))
        .length(10)
        .refine((items) => new Set(items.map((i) => i.dimension)).size === 10, 'Each rubric dimension must be rated once'),
    assistance: z.string().max(8000),
    feedback: z.string().min(20).max(16000),
});
export const fieldReadinessReportSchema = z.object({
    checkpointDay: z.union([z.literal(5), z.literal(10)]).optional(),
    engagementId: z.string(),
    candidateRevision: z.string(),
    moderatorId: z.string(),
    moderatedAt: z.string(),
    weightedScore: z.number(),
    passed: z.boolean(),
    ratings: fieldModerationSchema.shape.ratings,
    assistance: z.string(),
    feedback: z.string(),
});
export const fieldCapturePrSchema = z.object({
    requestId: z.uuid(),
    expectedRevision: z.number().int().nonnegative(),
    /** Present when saving a new native-workspace revision to an existing PR. */
    prId: id.optional(),
    title: z.string().min(1).max(200),
    description: z.string().max(12000),
    ticketIds: z.array(z.string()).max(20),
    reviewerIds: z.array(fieldActorIdSchema).min(1).max(5).optional(),
    capture: fieldWorkspaceCaptureRequestSchema,
});
